import { useState, useEffect, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, HttpError } from '../api';
import { useAuth } from '../auth';
import { ROLE_LABELS } from '../permissions';
import { Button } from '../design';

export function SettingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const qc = useQueryClient();

  const settingsQ = useQuery({ queryKey: ['ozon-settings'], queryFn: api.ozonSettings });

  const [clientId, setClientId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [premium, setPremium] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);

  useEffect(() => {
    if (settingsQ.data) {
      setClientId(settingsQ.data.clientId);
      setPremium(settingsQ.data.premium);
    }
  }, [settingsQ.data]);

  const save = useMutation({
    mutationFn: () => api.ozonSettingsSave({ clientId, apiKey: apiKey || undefined, premium }),
    onSuccess: () => {
      setSaved('Сохранено');
      setApiKey('');
      qc.invalidateQueries({ queryKey: ['ozon-settings'] });
      setTimeout(() => setSaved(null), 3000);
    },
  });

  const test = useMutation({ mutationFn: api.ozonSettingsTest });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  return (
    <>
      <div className="page-header"><h2>Настройки</h2></div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Ozon Seller API</h3>
        {settingsQ.isLoading ? 'Загрузка…' : (
          <form onSubmit={onSubmit}>
            <label style={{ display: 'block', marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Client-Id</div>
              <input
                type="text"
                required
                disabled={!isAdmin}
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                style={{ width: 320, padding: 8, border: '1px solid var(--border)', borderRadius: 6 }}
              />
            </label>

            <label style={{ display: 'block', marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
                Api-Key {settingsQ.data?.hasApiKey && <span className="muted">(сохранён: {settingsQ.data.apiKeyMasked} — оставь пустым чтобы не менять)</span>}
              </div>
              <input
                type="password"
                disabled={!isAdmin}
                placeholder={settingsQ.data?.hasApiKey ? '••••••••' : 'UUID из ЛК Ozon'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                style={{ width: 320, padding: 8, border: '1px solid var(--border)', borderRadius: 6 }}
              />
            </label>

            <label style={{ display: 'block', marginBottom: 16 }}>
              <input
                type="checkbox"
                disabled={!isAdmin}
                checked={premium}
                onChange={(e) => setPremium(e.target.checked)}
              />{' '}
              У магазина включён Ozon Premium (доступны расширенные метрики)
            </label>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {isAdmin && (
                <>
                  <Button type="submit" variant="primary" loading={save.isPending}>
                    Сохранить
                  </Button>
                  <Button type="button" variant="secondary" onClick={() => test.mutate()} loading={test.isPending} disabled={!settingsQ.data?.hasApiKey}>
                    Проверить подключение
                  </Button>
                </>
              )}
              {saved && <span style={{ color: 'var(--green)', fontSize: 13 }}>✓ {saved}</span>}
              {test.data && (
                <span style={{ fontSize: 13, color: test.data.ok ? 'var(--green)' : 'var(--red)' }}>
                  {test.data.ok ? `✓ Подключение работает (товаров: ${test.data.totalProducts})` : `✗ ${test.data.error}`}
                </span>
              )}
            </div>

            {settingsQ.data?.verifiedAt && (
              <div className="muted" style={{ fontSize: 12, marginTop: 12 }}>
                Последняя успешная проверка: {new Date(settingsQ.data.verifiedAt).toLocaleString('ru-RU')}
              </div>
            )}
            {settingsQ.data?.lastError && (
              <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 12 }}>
                Последняя ошибка: {settingsQ.data.lastError}
              </div>
            )}
          </form>
        )}
      </div>

      {isAdmin && <UsersBlock />}
    </>
  );
}

function UsersBlock() {
  const qc = useQueryClient();
  const usersQ = useQuery({ queryKey: ['users'], queryFn: api.users });
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', role: 'viewer' as 'admin' | 'manager' | 'warehouse' | 'viewer' });

  const create = useMutation({
    mutationFn: () => api.userCreate(newUser),
    onSuccess: () => {
      setCreating(false);
      setNewUser({ email: '', password: '', name: '', role: 'viewer' });
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const del = useMutation({
    mutationFn: api.userDelete,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) => api.userUpdate(id, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>Пользователи</h3>
        <Button variant="secondary" onClick={() => setCreating((v) => !v)}>{creating ? 'Отмена' : '+ Добавить'}</Button>
      </div>

      {creating && (
        <form
          onSubmit={(e) => { e.preventDefault(); create.mutate(); }}
          style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr) auto', gap: 8, marginBottom: 16, padding: 12, background: '#f9fafb', borderRadius: 6 }}
        >
          <input required placeholder="email" type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} style={{ padding: 8, border: '1px solid var(--border)', borderRadius: 6 }} />
          <input required placeholder="пароль (мин 8)" type="password" minLength={8} value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} style={{ padding: 8, border: '1px solid var(--border)', borderRadius: 6 }} />
          <input placeholder="имя (опц)" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} style={{ padding: 8, border: '1px solid var(--border)', borderRadius: 6 }} />
          <select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value as typeof newUser.role })} style={{ padding: 8, border: '1px solid var(--border)', borderRadius: 6 }}>
            <option value="admin">Администратор</option>
            <option value="manager">Менеджер маркетплейсов</option>
            <option value="warehouse">Сотрудник склада</option>
            <option value="viewer">Просмотр</option>
          </select>
          <Button type="submit" variant="primary" loading={create.isPending}>Создать</Button>
        </form>
      )}

      <table className="products">
        <thead>
          <tr><th>Email</th><th>Имя</th><th>Роль</th><th>Активен</th><th>Создан</th><th>Последний вход</th><th></th></tr>
        </thead>
        <tbody>
          {usersQ.data?.items.map((u) => (
            <tr key={u.id}>
              <td>{u.email}</td>
              <td>{u.name ?? '—'}</td>
              <td><span className="badge">{ROLE_LABELS[u.role as keyof typeof ROLE_LABELS] ?? u.role}</span></td>
              <td>
                <input type="checkbox" checked={u.active} onChange={(e) => toggleActive.mutate({ id: u.id, active: e.target.checked })} />
              </td>
              <td className="muted">{new Date(u.createdAt).toLocaleDateString('ru-RU')}</td>
              <td className="muted">{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString('ru-RU') : '—'}</td>
              <td>
                <Button variant="secondary" onClick={() => { if (confirm(`Удалить ${u.email}?`)) del.mutate(u.id); }}>Удалить</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
